import React, { useEffect, useMemo, useState } from 'react';
import {
  assignLscCommissioner,
  listLscCommissioners,
  listLscMembers,
  revokeLscCommissioner,
} from '../api';
import {
  COMMISSIONER_STAFF_COPY,
  getEffectiveLeagueRole,
  isLeagueCommissioner,
  leagueRoleBadgeClass,
  leagueRoleLabel,
  normalizeStoredRole,
} from '../leagueRoles';
import type { LscMember } from '../types';
import ErrorBanner from './ErrorBanner';

export interface ManageCommissionersPanelProps {
  leagueId: string;
  ownerUid?: string | null;
}

const ManageCommissionersPanel: React.FC<ManageCommissionersPanelProps> = ({
  leagueId,
  ownerUid,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staff, setStaff] = useState<Awaited<ReturnType<typeof listLscCommissioners>> | null>(
    null
  );
  const [members, setMembers] = useState<LscMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [staffResult, memberResult] = await Promise.all([
        listLscCommissioners(leagueId),
        listLscMembers(leagueId),
      ]);
      setStaff(staffResult);
      setMembers(memberResult.members);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load commissioners.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [leagueId]);

  const promotableMembers = useMemo(
    () =>
      members.filter((member) => {
        if (member.status !== 'active') return false;
        if (!member.user_uid) return false;
        if (member.user_uid === ownerUid) return false;
        const role = getEffectiveLeagueRole({
          membership: member,
          userUid: member.user_uid,
        });
        return !isLeagueCommissioner(role);
      }),
    [members, ownerUid]
  );

  const handleAssign = async () => {
    if (!selectedMemberId) return;
    setBusy(true);
    setError(null);
    try {
      await assignLscCommissioner(leagueId, selectedMemberId);
      setSelectedMemberId('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not assign Commissioner.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (memberId: string) => {
    setBusy(true);
    setError(null);
    try {
      await revokeLscCommissioner(leagueId, memberId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke Commissioner.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card space-y-4" data-testid="manage-commissioners-panel">
      <div>
        <h2 className="font-semibold text-white">Manage Commissioners</h2>
        <p className="text-sm text-secondary-400 mt-1">{COMMISSIONER_STAFF_COPY}</p>
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      {loading ? (
        <p className="text-sm text-secondary-400">Loading commissioners…</p>
      ) : (
        <>
          <div className="space-y-2">
            {(staff?.commissioners ?? []).map((member) => {
              const role = getEffectiveLeagueRole({
                membership: member,
                userUid: member.user_uid,
                effectiveRole: member.effective_role,
              });
              const isOwner = member.user_uid && member.user_uid === (staff?.ownerUid ?? ownerUid);
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-secondary-700 px-3 py-2"
                  data-testid={`commissioner-staff-row-${member.id}`}
                >
                  <div>
                    <p className="text-sm text-white">{member.display_name}</p>
                    <p className={`text-xs ${leagueRoleBadgeClass(role)}`}>
                      {isOwner ? 'League Owner' : leagueRoleLabel(role)}
                    </p>
                  </div>
                  {!isOwner && normalizeStoredRole(member.role) !== 'member' ? (
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={busy}
                      onClick={() => void handleRevoke(member.id)}
                      data-testid={`revoke-commissioner-${member.id}`}
                    >
                      Revoke
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="space-y-2 border-t border-secondary-700 pt-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-secondary-400">Promote active linked member</span>
              <select
                className="input-field"
                value={selectedMemberId}
                onChange={(event) => setSelectedMemberId(event.target.value)}
                disabled={busy || promotableMembers.length === 0}
                data-testid="manage-commissioners-promote-select"
              >
                <option value="">Select member…</option>
                {promotableMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.display_name}
                  </option>
                ))}
              </select>
            </label>
            {promotableMembers.length === 0 ? (
              <p className="text-xs text-secondary-500">
                This member must claim or link their profile before they can be made a Commissioner.
              </p>
            ) : null}
            <button
              type="button"
              className="btn-primary"
              disabled={!selectedMemberId || busy}
              onClick={() => void handleAssign()}
              data-testid="manage-commissioners-promote-button"
            >
              Make Commissioner
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageCommissionersPanel;
